/**
 * WalletSparkline — inline SVG sparkline para series temporales de balance.
 *
 *  - Auto-escala Y entre min y max (con pad para que los extremos no toquen
 *    los bordes).
 *  - Color de línea: verde si subió end-to-end, rojo si bajó, gris si plano
 *    (±0.1%).
 *  - Dibuja un dot en el último punto (el "ahora").
 *  - Hover crosshair + tooltip con date + balance (anteriormente solo
 *    SVG <title>, lo cual daba UX pobre — el browser tooltip tarda y se ve
 *    feo).
 *  - Si hay menos de 2 puntos (caso degenerado), muestra mensaje en lugar
 *    del SVG — el chart siempre debe ser leíble o no estar.
 *
 * No usa libs: una <path> + un <circle> + overlay interactivo. ~140 LOC.
 */

import { useRef, useState } from 'react'
import { CHART_COLORS, trendColor } from '../lib/chartColors'

export interface SparkPoint {
  date: string
  balance: number
}

interface Props {
  points: SparkPoint[]
  /** Width del viewBox (no pixels). El SVG se estira 100% via preserveAspectRatio. */
  width?: number
  /** Height del viewBox en pixels. */
  height?: number
  /** Color override (e.g. para tests). Default = verde/rojo/gris según trend. */
  color?: string
}

export function WalletSparkline({ points, width = 600, height = 80, color }: Props) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  if (points.length < 2) {
    return (
      <div className="text-eve-muted text-xs italic py-4 text-center">
        Datos insuficientes para graficar (se necesitan al menos 2 puntos).
      </div>
    )
  }

  const balances = points.map((p) => p.balance)
  const min = Math.min(...balances)
  const max = Math.max(...balances)
  const range = max - min || 1

  // Pad para que el primer/último punto no queden pegados al borde.
  const padX = 6
  const padY = 10
  const innerW = Math.max(1, width - 2 * padX)
  const innerH = Math.max(1, height - 2 * padY)

  const xAt = (i: number) => padX + (i / (points.length - 1)) * innerW
  const yAt = (b: number) => padY + (1 - (b - min) / range) * innerH

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(2)} ${yAt(p.balance).toFixed(2)}`)
    .join(' ')

  // Area para dar peso visual — fill con baja opacidad, mismo color que línea.
  const areaPath =
    `${linePath} L ${xAt(points.length - 1).toFixed(2)} ${height - padY}` +
    ` L ${xAt(0).toFixed(2)} ${height - padY} Z`

  const stroke = color ?? trendColor(points[0].balance, points[points.length - 1].balance)
  const lastIdx = points.length - 1
  const last = { x: xAt(lastIdx), y: yAt(points[lastIdx].balance) }
  const first = { x: xAt(0), y: yAt(points[0].balance) }

  // Hover handler — track index of nearest point under cursor.
  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    // Convertir pixel coord → viewBox coord (asumimos preserveAspectRatio="none").
    const xRatio = (e.clientX - rect.left) / rect.width
    const xInViewBox = xRatio * width
    // Encontrar el index más cercano por X.
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(xAt(i) - xInViewBox)
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    setHoverIdx(bestIdx)
  }

  const hoverPoint = hoverIdx !== null ? points[hoverIdx] : null
  const hoverX = hoverIdx !== null ? xAt(hoverIdx) : 0
  const hoverY = hoverIdx !== null ? yAt(points[hoverIdx].balance) : 0

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="overflow-visible block"
        role="img"
        aria-label={`Balance sparkline con ${points.length} puntos desde ${points[0].date} hasta ${points[points.length - 1].date}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Filled area debajo de la línea (mismo color, opacity 12%) */}
        <path d={areaPath} fill={stroke} fillOpacity={0.12} stroke="none" />
        {/* Línea */}
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Dot inicial (gris, chico) */}
        <circle cx={first.x} cy={first.y} r={2} fill={stroke} fillOpacity={0.4} />
        {/* Dot final (full color, más grande — el "ahora") */}
        <circle
          cx={last.x}
          cy={last.y}
          r={3.5}
          fill={stroke}
          stroke={CHART_COLORS.bg}
          strokeWidth={1.5}
        />
        {/* Hover crosshair + dot — only when hovering */}
        {hoverIdx !== null && (
          <g pointerEvents="none">
            <line
              x1={hoverX}
              y1={padY}
              x2={hoverX}
              y2={height - padY}
              stroke={CHART_COLORS.muted}
              strokeWidth={1}
              strokeOpacity={0.4}
              strokeDasharray="2 3"
            />
            <circle
              cx={hoverX}
              cy={hoverY}
              r={4}
              fill={stroke}
              stroke={CHART_COLORS.bg}
              strokeWidth={1.5}
            />
          </g>
        )}
      </svg>
      {/* HTML tooltip — positioned by viewBox units, converted via container ref. */}
      {hoverPoint && (
        <div
          className="absolute pointer-events-none bg-eve-panel border border-eve-border rounded-md px-2 py-1 text-[10px] font-mono shadow-lg whitespace-nowrap"
          style={{
            left: `${(hoverX / width) * 100}%`,
            top: `${(hoverY / height) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 8px))',
          }}
        >
          <div className="text-eve-muted">{hoverPoint.date}</div>
          <div className="text-eve-text font-semibold">
            {hoverPoint.balance.toLocaleString('en-US', { maximumFractionDigits: 0 })} ISK
          </div>
        </div>
      )}
    </div>
  )
}
